/**
 * 企微 H5 端 fetch 包装(2026-06-12 cookie cutover)。
 *
 * 与平台管理后台的 src/lib/api.ts 的差异:
 * - MEMBER JWT 在 HttpOnly cookie(rebuild OAuth 回调种;P0:token 不入 JS/URL),
 *   同域请求自动携带,JS 不再读写 token —— 旧 localStorage Authorization 模式随 legacy 退役
 * - 401 时:自动调 /wecom/oauth/url + 跳转 OAuth (不跳 /login)
 * - 1005 滑动续期:活跃会话由后端在响应里 Set-Cookie 换发同会话新票(HttpOnly),浏览器自动收下;
 *   本层无需处理(主域 httpClient 的 X-Renewed-Token 换票只给 Bearer 会话)。前提是请求带 cookie。
 *
 * appConfigId 是触发重 OAuth 必需的;由 useWeComAuth 在初次拿到 URL 参数时调用
 * setAppConfigId(id) 持久化到 localStorage,方便 401 自动续签时复原。
 */

import { maskVendor } from "@/shared/lib/vendorMask";
import { ApiError, nonJsonResponseError } from "@/shared/api";
import { UNKNOWN_ERROR_TITLE } from "@/shared/lib/apiErrors";
import { reportApiServerError } from "@/shared/lib/errorReporting";

const API_BASE = "/api/v1";
const APP_CONFIG_KEY = "wecom_app_config_id";

let inMemoryAppConfigId: string | null = null;

export function setAppConfigId(id: string): void {
  inMemoryAppConfigId = id;
  try {
    localStorage.setItem(APP_CONFIG_KEY, id);
  } catch {
    // localStorage 不可用时忽略,在内存里仍然记着即可
  }
}

export function getAppConfigId(): string | null {
  if (inMemoryAppConfigId) return inMemoryAppConfigId;
  try {
    return localStorage.getItem(APP_CONFIG_KEY);
  } catch {
    return null;
  }
}

export function clearMemberToken(): void {
  // cookie 是 HttpOnly,JS 清不掉;保留空实现给既有调用方,真实失效靠 cookie 过期/后端踢
}

/** 仅供测试:重置模块级状态 */
export function _resetForTest(): void {
  inMemoryAppConfigId = null;
}

class UnauthorizedError extends Error {
  constructor(message = "Unauthorized") {
    super(message);
    this.name = "UnauthorizedError";
  }
}

async function reOAuth(): Promise<never> {
  const appConfigId = getAppConfigId();
  if (!appConfigId) {
    throw new UnauthorizedError("登录已过期且无法自动重新授权(缺少 appConfigId,请从入口重新进入)");
  }
  const res = await fetch(
    `${API_BASE}/wecom/oauth/url?appConfigId=${encodeURIComponent(appConfigId)}`
  );
  let json: { success?: boolean; data?: string; message?: string };
  try {
    json = await res.json();
  } catch {
    throw new UnauthorizedError("登录已过期,获取重新授权链接失败");
  }
  if (!json.success || typeof json.data !== "string") {
    throw new UnauthorizedError(json.message ?? "登录已过期,获取重新授权链接失败");
  }
  window.location.href = json.data;
  // 抛出特殊错误终止调用链;浏览器在导航生效后页面会被替换,上层无需处理此错误
  throw new UnauthorizedError("正在跳转授权页面…");
}

interface RequestOptions {
  method?: "GET" | "POST" | "PUT" | "DELETE" | "PATCH";
  body?: unknown;
  headers?: Record<string, string>;
}

async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const headers: Record<string, string> = { ...(options.headers ?? {}) };
  // FormData(语音陪练录音上传)不手设 Content-Type —— 浏览器自动带 multipart boundary
  const isForm = typeof FormData !== "undefined" && options.body instanceof FormData;
  if (options.body !== undefined && !isForm && !headers["Content-Type"]) {
    headers["Content-Type"] = "application/json";
  }

  const method = options.method ?? (options.body !== undefined ? "POST" : "GET");
  // MEMBER JWT 在 HttpOnly cookie,同域 fetch 默认 same-origin 自动携带,无需 Authorization
  const res = await fetch(`${API_BASE}${path}`, {
    method,
    headers,
    credentials: "same-origin",
    body:
      options.body !== undefined
        ? isForm
          ? (options.body as FormData)
          : JSON.stringify(options.body)
        : undefined,
  });

  if (res.status === 401) {
    await reOAuth(); // never returns (redirects)
  }

  let json: { success?: boolean; data?: unknown; message?: string; code?: string };
  try {
    json = await res.json();
  } catch {
    reportApiServerError({ path, status: res.status, method, client: "wecom" });
    // ★0926 修复波第三批:网关 HTML 502/504、nginx 413 等非 JSON 响应不再抛「请求失败 (N)」,
    //   与主域 httpClient 同口径给人话(H5 各处直接显示 e.message)。
    throw nonJsonResponseError(res.status, path);
  }
  if (!json.success) {
    reportApiServerError({
      path,
      status: res.status,
      method,
      errorCode: json.code,
      client: "wecom",
    });
    throw new ApiError(
      maskVendor(json.message) || UNKNOWN_ERROR_TITLE,
      json.code || "UNKNOWN",
      res.status
    );
  }
  return json.data as T;
}

export const wecomApi = {
  get: <T>(path: string) => request<T>(path),
  post: <T>(path: string, body?: unknown) => request<T>(path, { method: "POST", body }),
  /** multipart 上传(V29 语音陪练录音);FormData 直传,浏览器自带 boundary。 */
  postForm: <T>(path: string, form: FormData) => request<T>(path, { method: "POST", body: form }),
  put: <T>(path: string, body?: unknown) => request<T>(path, { method: "PUT", body }),
  delete: <T>(path: string) => request<T>(path, { method: "DELETE" }),
};
